import { readContract } from '@wagmi/core'
import { useEffect, useMemo, useState } from 'react'

import { STAKE_ADDRESS } from '@/constants/blockchain'
import Stake from '@/contracts/Stake'
import { useCustomContractRead, useCustomContractReadBalance } from '@/hooks/useCustomContract'
import { useAuth } from '@/plugins/auth'
import { getWagmiConfig, l1NativeConfig, l1NativeWagmiConfig } from '@/plugins/auth/config'
import { CustomError, parseError } from '@/utils/helpers'
import { emptyBalance, processBalance, ProcessedBalance } from '@/utils/utils'

import { useChainValues } from './useChain'

export type StakedItem = {
  day: number
  amount: ProcessedBalance
  stake: ProcessedBalance
  left: number
}

export type StakingHook = {
  staked: ProcessedBalance
  stake: ProcessedBalance
  stakedList: StakedItem[] | undefined
  claimableRewards: ProcessedBalance
  isLoading: boolean
  error: CustomError
}

export const EmptyStaking: StakingHook = {
  staked: emptyBalance,
  stake: emptyBalance,
  stakedList: undefined,
  claimableRewards: emptyBalance,
  isLoading: false,
  error: {
    call: '',
    msg: undefined,
    isError: false,
  },
}

const useGetUnlockableAndBurnableAmounts = (
  address: `0x${string}`,
  days?: readonly number[],
  deps?: any[]
) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [value, setValue] = useState<[ProcessedBalance, ProcessedBalance][]>()
  const config = getWagmiConfig()

  // Get unlockable + burnable amounts
  const getAmounts = async (): Promise<void> => {
    try {
      if (!days) {
        return
      }
      setIsLoading(true)
      const unlockable = await Promise.all(
        days.map(day =>
          readContract(config, {
            chainId: l1NativeConfig.chainId,
            address: STAKE_ADDRESS,
            abi: Stake,
            functionName: 'unlockable',
            args: [address, day],
          })
        )
      )
      const burnable = await Promise.all(
        days.map(day =>
          readContract(config, {
            chainId: l1NativeConfig.chainId,
            address: STAKE_ADDRESS,
            abi: Stake,
            functionName: 'burnable',
            args: [address, day],
          })
        )
      )
      setValue(unlockable.map((u, i) => [processBalance(u), processBalance(burnable[i])]))
      setIsError(false)
    } catch (error: any) {
      setIsError(true)
    }
    setIsLoading(false)
  }

  // Fetch on changes to address or days
  useEffect(() => {
    if (address && days && days.length > 0) {
      getAmounts()
    }
  }, [address, days, ...(deps || [])])

  return {
    value,
    isLoading,
    error: { call: 'getUnlockableAmounts', msg: isError ? '' : undefined, isError },
  }
}

const maxDays = 30

const useGetRewards = (address: `0x${string}`, startDay?: number, endDay?: number) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [value, setValue] = useState<ProcessedBalance>(processBalance(BigInt(0)))
  const config = getWagmiConfig()

  // Get rewards in batches if necessary
  const getRewards = async (start: number, end: number): Promise<void> => {
    try {
      if (start > end) {
        setValue(processBalance(BigInt(0)))
        return
      }
      setIsLoading(true)
      const numBatches = Math.ceil((1 + end - start) / maxDays)
      const batches: [number, number][] = []
      for (let i = 0; i < numBatches; i++) {
        const batchStart = start + i * maxDays
        const batchEnd = Math.min(start + (i + 1) * maxDays, end)
        batches.push([batchStart, batchEnd])
      }
      const values = await Promise.all(
        batches.map(([s, e]) =>
          readContract(config, {
            chainId: l1NativeConfig.chainId,
            address: STAKE_ADDRESS,
            abi: Stake,
            functionName: 'claimableRL1Range',
            args: [address, s, e],
          })
        )
      )
      const total = values.reduce((acc, val) => acc + (val as bigint), BigInt(0))
      setValue(processBalance(total))
      setIsError(false)
    } catch (error: any) {
      setIsError(true)
    }
    setIsLoading(false)
  }

  // Fetch on changes to start day or last day
  useEffect(() => {
    if (address && startDay !== undefined && endDay !== undefined) {
      getRewards(startDay, endDay)
    }
  }, [address, startDay, endDay])

  return {
    value,
    isLoading,
    error: { call: 'claimableRL1Range', msg: isError ? '' : undefined, isError },
  }
}

export const useStaking = (): StakingHook => {
  const { address } = useAuth()

  // Get block number
  const { blockNumber } = useChainValues(l1NativeWagmiConfig.id)

  // User staked balance
  const {
    value: staked,
    isLoading: isLoadingStaked,
    error: errorStaked,
  } = useCustomContractReadBalance(
    {
      chainId: l1NativeWagmiConfig.id,
      address: STAKE_ADDRESS,
      args: [address as `0x${string}`],
      abi: Stake,
      functionName: 'locked',
    },
    blockNumber,
    !!address
  )

  // User stake balance
  const {
    value: stake,
    isLoading: isLoadingStake,
    error: errorStake,
  } = useCustomContractReadBalance(
    {
      chainId: l1NativeWagmiConfig.id,
      address: STAKE_ADDRESS,
      args: [address as `0x${string}`],
      abi: Stake,
      functionName: 'balanceOf',
    },
    blockNumber,
    !!address
  )

  // Check if staking has started
  const {
    value: started,
    isLoading: isLoadingHasStarted,
    error: errorHasStarted,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: STAKE_ADDRESS,
      args: [],
      abi: Stake,
      functionName: 'hasStarted',
    },
    blockNumber
  )

  // Today
  const { value: today, isLoading: isLoadingToday } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: STAKE_ADDRESS,
      args: [],
      abi: Stake,
      functionName: 'today',
    },
    blockNumber,
    started || false
  )

  // User unlockable days
  const {
    value: unlockableDays,
    isLoading: isLoadingunlockableDays,
    error: errorunlockableDays,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: STAKE_ADDRESS,
      args: [address as `0x${string}`],
      abi: Stake,
      functionName: 'getUnlockableDays',
    },
    blockNumber,
    !!address
  )

  // User unlockable + burnable amounts
  const {
    value: unlockableAmounts,
    isLoading: isLoadingUnlockableAmounts,
    error: errorUnlockableAmounts,
  } = useGetUnlockableAndBurnableAmounts(address as `0x${string}`, unlockableDays, [stake])
  // NOTE: pass stake as a dependency to recalculate unlockableAmounts when stake token value changes
  // This is necessary on withdraw, since stake tokens are burnt but unlockableDays doesn't change
  // On staking, unlockableDays changes, so it should be fine

  // User staked list
  const stakedList: StakedItem[] | undefined = useMemo(() => {
    if (
      today !== undefined &&
      unlockableDays &&
      unlockableAmounts &&
      unlockableDays.length === unlockableAmounts.length
    ) {
      return unlockableDays
        .map((day, i) => ({
          day,
          amount: unlockableAmounts[i][0],
          stake: unlockableAmounts[i][1],
          left: day - today, // days left to unlock (negative means stake can be withdrawn)
        }))
        .filter(({ amount }) => amount?.value > 0) // Filter out 0 amounts (already withdrawn)
        .sort((a, b) => a.day - b.day) // Sort by day
    }
    return undefined
  }, [today, unlockableDays, unlockableAmounts])

  // Compute user available rewards

  // Get first day user staked
  // This comes from stake balance changes index 0
  const {
    value: stakeBalanceChanges,
    isLoading: isLoadingStakeBalanceChanges,
    error: errorStakeBalanceChanges,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: STAKE_ADDRESS,
      args: [address as `0x${string}`],
      abi: Stake,
      functionName: 'getStakeBalanceChanges',
    },
    blockNumber,
    !!address
  )

  // Get user largest claimed day
  const {
    value: largestClaimedDay,
    isLoading: isLoadingLargestClaimedDay,
    error: errorLargestClaimedDay,
  } = useCustomContractRead(
    {
      chainId: l1NativeWagmiConfig.id,
      address: STAKE_ADDRESS,
      args: [address as `0x${string}`],
      abi: Stake,
      functionName: 'largestClaimedDay',
    },
    blockNumber,
    !!address
  )

  // Start day
  // This is the first day the user staked OR the largest claimed day + 1
  // if user has claimed before
  const startDay = useMemo(() => {
    if (largestClaimedDay && largestClaimedDay > 0) {
      return largestClaimedDay + 1
    }
    if (stakeBalanceChanges && stakeBalanceChanges.length > 0) {
      return stakeBalanceChanges[0].day
    }
    return undefined
  }, [largestClaimedDay, stakeBalanceChanges])

  // Last day
  const lastDay = useMemo(() => {
    if (today) {
      return today - 1
    }
    if (today === 0) {
      return 0
    }
    return undefined
  }, [today])

  // Compute claimable rewards
  const {
    value: claimableRewards,
    isLoading: isLoadingClaimableRewards,
    error: errorClaimableRewards,
  } = useGetRewards(address as `0x${string}`, startDay, lastDay)

  // Loading
  const isLoading = useMemo(
    () =>
      isLoadingStaked ||
      isLoadingStake ||
      isLoadingHasStarted ||
      isLoadingToday ||
      isLoadingunlockableDays ||
      isLoadingUnlockableAmounts ||
      isLoadingStakeBalanceChanges ||
      isLoadingLargestClaimedDay ||
      isLoadingClaimableRewards,
    [
      isLoadingStaked,
      isLoadingStake,
      isLoadingHasStarted,
      isLoadingToday,
      isLoadingunlockableDays,
      isLoadingUnlockableAmounts,
      isLoadingStakeBalanceChanges,
      isLoadingLargestClaimedDay,
      isLoadingClaimableRewards,
    ]
  )

  // Error
  const errors = [
    errorStaked,
    errorStake,
    errorHasStarted,
    errorunlockableDays,
    errorUnlockableAmounts,
    errorStakeBalanceChanges,
    errorLargestClaimedDay,
    errorClaimableRewards,
  ]
  const error = useMemo(() => parseError(errors), errors)

  return {
    staked,
    stake,
    stakedList,
    claimableRewards,
    isLoading,
    error: {
      call: `staking.${error.call}`,
      msg: error.msg,
      isError: error.isError,
    },
  }
}
